import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, addDays } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { ActivityChartSkeleton } from "./ActivityChartSkeleton";
import { useIsMobile } from "@/hooks/use-mobile";

export const ActivityChart = () => {
  const { user } = useAuth();
  const [hoveredDay, setHoveredDay] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Add window resize listener
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch all user's submitted drafts
  const { data: submittedDrafts, isLoading } = useQuery({
    queryKey: ['activity-drafts', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('drafts')
        .select(`
          created_at,
          is_submitted,
          topics(task_type)
        `)
        .eq('user_id', user.id)
        .eq('is_submitted', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000
  });

  // Generate days for current month
  const daysInMonth = useMemo(() => {
    const today = new Date();
    const firstDay = startOfMonth(today);
    const lastDay = endOfMonth(today);

    return eachDayOfInterval({ start: firstDay, end: lastDay })
      .map(date => format(date, 'yyyy-MM-dd'));
  }, []);

  // Get the current month name
  const currentMonthName = useMemo(() => {
    return format(new Date(), 'MMMM yyyy');
  }, []);

  // Determine layout based on screen size and month length
  const layout = useMemo(() => {
    // Get the number of days in the month
    const numDays = daysInMonth.length;

    if (isMobile) {
      return { rows: 3, cols: Math.ceil(numDays / 3) };
    } else {
      if (windowWidth >= 1200) {
        return { rows: 1, cols: numDays }; // Single row for large screens
      } else {
        return { rows: 2, cols: Math.ceil(numDays / 2) }; // Two rows for medium screens
      }
    }
  }, [isMobile, windowWidth, daysInMonth.length]);

  // Group the days into rows based on layout
  const rows = useMemo(() => {
    const result = [];
    const { rows: numRows, cols: numCols } = layout;

    // Calculate days per row, ensuring all days fit
    const daysPerRow = Math.ceil(daysInMonth.length / numRows);

    for (let i = 0; i < daysInMonth.length; i += daysPerRow) {
      result.push(daysInMonth.slice(i, i + daysPerRow));
    }

    return result;
  }, [daysInMonth, layout]);

  // Generate week markers
  const weekMarkers = useMemo(() => {
    const markers = [];
    const firstDayOfMonth = startOfMonth(new Date());

    // Find the start of the week that contains the first day of the month
    let currentWeekStart = startOfWeek(firstDayOfMonth);

    // Generate markers for each week in the month
    const lastDayOfMonth = endOfMonth(new Date());
    while (currentWeekStart <= lastDayOfMonth) {
      markers.push(format(currentWeekStart, 'yyyy-MM-dd'));
      currentWeekStart = addDays(currentWeekStart, 7);
    }

    return markers;
  }, []);

  // Find days with completed tasks
  const completedDays = useMemo(() => {
    if (!submittedDrafts) return new Set<string>();

    const daysSet = new Set<string>();
    submittedDrafts.forEach(draft => {
      const date = format(new Date(draft.created_at), 'yyyy-MM-dd');
      daysSet.add(date);
    });
    return daysSet;
  }, [submittedDrafts]);

  if (isLoading) {
    return <ActivityChartSkeleton />;
  }

  // Function to check if a day is a week marker
  const isWeekMarker = (day: string) => weekMarkers.includes(day);

  return (
    <Card className="border border-stone-200 shadow-sm dark:bg-stone-800 dark:border-stone-700">
      <CardHeader className="px-8">
        <CardTitle className="text-xl dark:text-white">Your activity this {format(new Date(), 'MMMM')}</CardTitle>
      </CardHeader>
      <CardContent className="px-8 pb-6">
        <div className="space-y-8">
          <div className="flex flex-col space-y-6">
            {rows.map((row, rowIndex) => (
              <div key={rowIndex} className="flex justify-between">
                {row.map(day => {
                  const hasActivity = completedDays.has(day);
                  const isHovered = day === hoveredDay;
                  const isWeekStart = isWeekMarker(day);
                  const dayOfMonth = parseInt(format(parseISO(day), 'd'));

                  return (
                    <TooltipProvider key={day}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={`relative w-6 h-6 rounded-sm border flex items-center justify-center ${hasActivity
                              ? 'bg-orange-500 border-orange-600 shadow-[inset_0px_1px_0px_0px_#FFFFFF66,0px_0px_0px_1px_#C2410CCC]'
                              : 'bg-stone-100 border-stone-200 dark:bg-stone-700 dark:border-stone-600'
                              } transition-colors duration-200 cursor-pointer ${isHovered && !hasActivity ? 'border-orange-500' : ''
                              }`}
                            onMouseEnter={() => setHoveredDay(day)}
                            onMouseLeave={() => setHoveredDay(null)}
                          >
                            <span className={`text-xs text-center font-medium ${hasActivity ? 'text-white' : 'text-stone-600 dark:text-stone-300'
                              }`}>
                              {dayOfMonth}
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="font-medium">
                            {format(parseISO(day), 'MMM d, yyyy')}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {hasActivity
                              ? 'Completed task(s)'
                              : 'No activity'
                            }
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};